import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Ion } from "@/components/Ion";
import { colors, useTheme } from "@/lib/theme";

const Scrim = ({ dark = 0.78, children }) => <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" style={{ background: `rgba(0,0,0,${dark})` }}>{children}</div>;

const CAT = { feature: "Feature", bug: "Bug Fix", ui: "Polish", integration: "Integration", other: "Update" };
const SESS = "alr_whats_new_dismissed_at_login";

export const WhatsNewModal = () => {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!user || !["franchisee", "admin"].includes(user.role) || sessionStorage.getItem(`${SESS}:${user.id}`) === "1") return;
    api("/feedback/whats-new?days=30&limit=10").then((r) => r.items?.length && setItems(r.items)).catch(() => {});
  }, [user]);
  if (!items?.length) return null;
  const dismiss = () => { sessionStorage.setItem(`${SESS}:${user.id}`, "1"); setItems(null); };
  const yours = items.filter((i) => i.is_yours).length;
  return (
    <Scrim dark={0.75}>
      <div className="flex max-h-[90%] w-full max-w-[560px] flex-col rounded-[14px] border border-[#333] bg-[#121212] p-4" data-testid="whats-new-modal">
        <div className="mb-3 flex items-center">
          <Ion name="sparkles" size={22} color={colors.accent} />
          <div className="ml-2.5 flex-1"><p className="text-[10px] font-extrabold tracking-[1.5px] text-[#4CCC06]">WHAT'S NEW IN THE PORTAL</p><p className="mt-0.5 text-[17px] font-extrabold">Shaped by your feedback</p></div>
          <button onClick={dismiss} data-testid="whats-new-close"><Ion name="close" size={22} color={colors.textSecondary} /></button>
        </div>
        {yours > 0 && <div className="mb-2.5 flex items-center rounded-lg border border-[#4CCC06] bg-[#4CCC061A] p-2.5 text-xs font-semibold leading-[17px]"><Ion name="heart" size={14} color={colors.accent} />&nbsp;&nbsp;{yours === 1 ? "One of these was your idea!" : `${yours} of these were your ideas!`} Thank you for shaping the portal.</div>}
        <div className="max-h-[380px] overflow-y-auto pb-1">
          {items.map((it) => {
            const dot = it.status === "added" ? "#22c55e" : "#3b82f6";
            return (
              <div key={it.id} className="mb-2 flex rounded-lg border p-3" style={{ borderColor: it.is_yours ? colors.accent : colors.border, background: it.is_yours ? `${colors.accent}0F` : colors.bg }}>
                <span className="mr-2.5 mt-1.5 h-2 w-2 shrink-0 rounded-full" style={{ background: dot }} />
                <div className="flex-1">
                  <div className="flex flex-wrap items-center">
                    <span className="mr-1.5 rounded border px-1.5 py-0.5 text-[9px] font-extrabold tracking-[1px]" style={{ borderColor: dot, color: dot }}>{it.status === "added" ? "ADDED" : "FIXED"}</span>
                    <span className="text-[10px] font-bold tracking-[1px] text-[#A0A0A0]">{CAT[it.category] || "Update"}</span>
                    {it.is_yours && <span className="ml-1.5 flex items-center rounded bg-[#4CCC06] px-1.5 py-0.5 text-[9px] font-black tracking-[1px] text-black"><Ion name="ribbon" size={10} color="#000" />&nbsp;&nbsp;YOUR IDEA</span>}
                  </div>
                  <p className="mt-1 text-sm font-bold">{it.title}</p>
                  {it.public_note && <p className="mt-1 text-xs leading-[17px] text-[#A0A0A0]">{it.public_note}</p>}
                </div>
              </div>
            );
          })}
        </div>
        <button onClick={dismiss} data-testid="whats-new-continue" className="mt-3 flex items-center justify-center rounded-lg bg-[#4CCC06] py-3.5 text-[13px] font-black tracking-[1px] text-black">CONTINUE TO DASHBOARD&nbsp;<Ion name="arrow-forward" size={16} color="#000" /></button>
        <p className="mt-2.5 text-center text-[10px] leading-[14px] text-[#A0A0A0]">Have an idea? Tap the <b className="font-extrabold text-white">Suggest a Feature</b> tile — Greg reads every one.</p>
      </div>
    </Scrim>
  );
};

export const CeoSuccessModal = () => {
  const { user } = useAuth();
  const [msg, setMsg] = useState(null);
  const [ack, setAck] = useState(false);
  useEffect(() => {
    if (!user || !["franchisee", "admin"].includes(user.role)) return;
    api("/ceo-message/current").then((r) => r.message && setMsg(r.message)).catch(() => {});
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!msg) return null;
  const onContinue = async () => {
    if (ack) return;
    setAck(true);
    try { await api("/ceo-message/acknowledge", { method: "POST" }); } catch {}
    setMsg(null); setAck(false);
  };
  return (
    <Scrim>
      <div className="flex w-full max-w-[460px] flex-col items-center gap-3.5 rounded-[14px] border-2 border-[#4CCC06] bg-[#1A1A1A] p-6" style={{ boxShadow: "0 18px 60px rgba(0,0,0,0.6)" }} data-testid="ceo-success-modal">
        <span className="mb-1 flex h-14 w-14 items-center justify-center rounded-full bg-[#4CCC06]"><Ion name="trophy" size={26} color="#000" /></span>
        <p className="text-center text-[10.5px] font-black tracking-[2.2px] text-[#4CCC06]">DAILY CEO SUCCESS MESSAGE</p>
        <p className="px-1 text-center text-lg font-bold leading-[27px]">{msg.text}</p>
        <span className="my-1 h-0.5 w-12 rounded-sm bg-[#333]" />
        <button onClick={onContinue} disabled={ack} data-testid="ceo-continue-btn" className="flex min-w-[240px] items-center justify-center gap-2 rounded-lg bg-[#4CCC06] px-6 py-3.5 text-[13.5px] font-black tracking-[0.8px] text-black disabled:opacity-50">
          {ack ? <Loader2 className="animate-spin" size={18} /> : <>CONTINUE TO DASHBOARD <Ion name="arrow-forward" size={16} color="#000" /></>}
        </button>
        <p className="mt-1 text-center text-[10.5px] italic text-[#A0A0A0]">Think Different. Clean Different. Lead Different.</p>
      </div>
    </Scrim>
  );
};

export const ReleaseNotesModal = () => {
  const theme = useTheme();
  const [entries, setEntries] = useState(null);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api("/release-notes/unseen").then((r) => r.unseen?.length && setEntries(r.unseen)).catch(() => {}); }, []);
  if (!entries) return null;
  const confirm = async () => {
    if (!checked || busy) return;
    setBusy(true);
    try { await api("/release-notes/ack", { method: "POST" }); } catch {}
    setBusy(false); setEntries(null);
  };
  return (
    <Scrim>
      <div className="flex max-h-[88%] w-full max-w-[480px] flex-col rounded-[14px] border p-6" style={{ background: theme.card, borderColor: `${theme.accent}55` }} data-testid="release-notes-modal">
        <div className="mb-4 flex"><span className="flex items-center rounded-full border px-2.5 py-[5px] text-[10px] font-extrabold tracking-[2px]" style={{ color: theme.accent, background: `${theme.accent}1A`, borderColor: `${theme.accent}55` }}><Ion name="sparkles" size={14} color={theme.accent} />&nbsp;&nbsp;WHAT'S NEW</span></div>
        <h2 className="mb-1.5 text-[22px] font-extrabold">Fresh updates just landed 🎉</h2>
        <p className="mb-4 text-[13px] leading-[19px]" style={{ color: theme.textSecondary }}>Here's everything we've added since you were last here. Quick read — promise!</p>
        <div className="max-h-[360px] overflow-y-auto border-y pb-4 pt-4" style={{ borderColor: theme.border }}>
          {entries.map((e) => (
            <div key={e.version} className="mb-6">
              <div className="mb-1 flex items-center gap-2"><span className="rounded-md px-2 py-0.5 text-[10px] font-extrabold tracking-[0.6px]" style={{ color: theme.accent, background: `${theme.accent}22` }}>v{e.version}</span><span className="text-[11px] font-semibold" style={{ color: theme.textSecondary }}>{e.date}</span></div>
              <p className="mb-2 text-[15px] font-extrabold">{e.title}</p>
              {e.highlights.map((h, i) => <div key={i} className="mb-1.5 flex items-start pr-1"><Ion name="checkmark-circle" size={14} color={theme.accent} style={{ marginTop: 2 }} /><span className="flex-1 text-[13px] leading-[19px]">&nbsp;&nbsp;{h}</span></div>)}
            </div>
          ))}
        </div>
        <button onClick={() => setChecked((v) => !v)} data-testid="release-notes-checkbox-row" className="flex items-center gap-3 py-4 text-left">
          <span className="flex h-[22px] w-[22px] items-center justify-center rounded-md border-2" style={checked ? { background: theme.accent, borderColor: theme.accent } : { borderColor: theme.border, background: theme.surface }}>{checked && <Ion name="checkmark" size={14} color="#000" />}</span>
          <span className="flex-1 text-[13px] leading-[18px]">Got it — I've read what's new and I'm ready to dive in.</span>
        </button>
        <button onClick={confirm} disabled={!checked || busy} data-testid="release-notes-confirm" className="flex items-center justify-center gap-2 rounded-lg py-3.5 text-sm font-extrabold text-black disabled:opacity-40" style={{ background: theme.accent }}>
          {busy ? <Loader2 className="animate-spin" size={18} /> : <>Okay, take me to my dashboard <Ion name="arrow-forward" size={18} color="#000" /></>}
        </button>
      </div>
    </Scrim>
  );
};

function badgeVisual(s) {
  if (!s) return null;
  const p = (d) => (d === 1 ? "" : "s");
  switch (s.status) {
    case "ok": { const d = s.days_remaining ?? 0; const c = d <= 90 ? ["rgba(250,204,21,0.15)", "#FACC15"] : ["rgba(52,211,153,0.15)", "#34D399"]; return { bg: c[0], fg: c[1], icon: "shield-checkmark", label: `Insurance: ${d} day${p(d)} left` }; }
    case "expiring": { const d = s.days_remaining ?? 0; return { bg: "rgba(251,146,60,0.15)", fg: "#FB923C", icon: "alert-circle", label: `Renew now: ${d} day${p(d)} left` }; }
    case "needs_update": { const left = Math.max(0, (s.grace_days ?? 30) - Math.abs(s.days_remaining ?? 0)); return { bg: "rgba(239,68,68,0.15)", fg: "#EF4444", icon: "warning", label: `Expired — ${left} day${p(left)} to renew` }; }
    case "new_grace": { const d = s.days_until_default ?? 0; return { bg: "rgba(96,165,250,0.15)", fg: "#60A5FA", icon: "cloud-upload", label: `Upload policy: ${d} day${p(d)} left` }; }
    case "pending_review": return { bg: "rgba(167,139,250,0.18)", fg: "#A78BFA", icon: "hourglass", label: "Renewal pending review" };
    case "default": return { bg: "rgba(239,68,68,0.20)", fg: "#EF4444", icon: "lock-closed", label: "ACCOUNT IN DEFAULT — TAP" };
    default: return null;
  }
}

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

export const InsuranceCard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  useEffect(() => { if (user?.role === "franchisee") api("/insurance/status").then(setS).catch(() => {}); }, [user]);
  const v = user?.role === "franchisee" ? badgeVisual(s) : null;
  if (!v) return null;
  const detail = s.expires_at && ["ok", "expiring"].includes(s.status) ? `Policy expires ${fmtDate(s.expires_at)}` : s.status === "needs_update" ? `Policy expired ${fmtDate(s.expires_at)}` : s.status === "new_grace" ? "Upload your current policy to keep full access" : "";
  return (
    <button onClick={() => navigate("/insurance")} data-testid="insurance-card" className="mb-6 flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition-transform duration-150 hover:-translate-y-0.5" style={{ background: v.bg, borderColor: v.fg }}>
      <Ion name={v.icon} size={22} color={v.fg} />
      <span className="flex-1">
        <span className="block text-[9px] font-extrabold tracking-[1.6px]" style={{ color: v.fg }}>INSURANCE</span>
        <span className="block text-[14px] font-extrabold text-white" data-testid="insurance-card-label">{v.label}</span>
        {detail && <span className="block text-xs text-neutral-300" data-testid="insurance-card-detail">{detail}</span>}
      </span>
      <span className="text-[11px] font-extrabold" style={{ color: v.fg }}>{s.status === "ok" ? "View" : "Update"} →</span>
    </button>
  );
};

const WATCH = ["default", "needs_update", "pending_review", "expiring", "new_grace"];
export const AdminInsuranceWatch = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => { api("/insurance/admin/all").then((r) => setRows(r.users.filter((u) => WATCH.includes(u.status) && !(u.email || "").endsWith("@alrtest.com")))).catch(() => setRows([])); }, []);
  if (!rows) return null;
  return (
    <div data-testid="admin-insurance-watch" className="mb-6 rounded-xl border p-4" style={{ background: "#141414", borderColor: rows.length ? "#FB923C" : "#333" }}>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[11px] font-extrabold tracking-[2px]" style={{ color: rows.length ? "#FB923C" : "#4CCC06" }}>INSURANCE · NEXT 30 DAYS</span>
        <button onClick={() => navigate("/admin/insurance")} data-testid="admin-insurance-watch-open" className="text-[11px] font-extrabold text-[#4CCC06]">Open insurance reviews →</button>
      </div>
      {rows.length === 0 ? <p className="text-sm text-neutral-300" data-testid="admin-insurance-watch-empty">Every franchisee's policy is current for the next 30 days.</p> : (
        <div className="flex flex-col divide-y divide-[#2a2a2a]">
          {rows.slice(0, 8).map((u) => { const v = badgeVisual(u); return (
            <div key={u.id} data-testid={`admin-insurance-row-${u.id}`} className="flex items-center gap-3 py-2">
              <span className="flex-1 truncate text-sm font-bold text-white">{u.full_name || u.email}</span>
              {u.expires_at && <span className="hidden text-xs text-neutral-400 sm:inline">expires {fmtDate(u.expires_at)}</span>}
              <span className="rounded-full border px-2 py-0.5 text-[11px] font-extrabold" style={{ color: v?.fg, borderColor: v?.fg }}>{v?.label}</span>
            </div>
          ); })}
          {rows.length > 8 && <p className="pt-2 text-xs text-neutral-400">+{rows.length - 8} more in insurance reviews</p>}
        </div>
      )}
    </div>
  );
};

export const InsuranceCountdownBadge = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  useEffect(() => { if (user?.role === "franchisee") api("/insurance/status").then(setStatus).catch(() => {}); }, [user, pathname]);
  const v = user?.role === "franchisee" ? badgeVisual(status) : null;
  if (!v) return null;
  return (
    <button onClick={() => navigate("/insurance")} data-testid="insurance-countdown-badge" className="flex max-w-[240px] items-center gap-1.5 self-start rounded-full border px-2.5 py-1.5" style={{ background: v.bg, borderColor: v.fg }}>
      <Ion name={v.icon} size={14} color={v.fg} /><span className="truncate text-[11px] font-extrabold tracking-[0.4px]" style={{ color: v.fg }}>{v.label}</span>
    </button>
  );
};

const LANGS = [
  ["en", "English", "English", "🇺🇸"], ["es", "Español", "Spanish", "🇪🇸"], ["zh", "中文", "Chinese", "🇨🇳"], ["hi", "हिन्दी", "Hindi", "🇮🇳"],
  ["ar", "العربية", "Arabic", "🇸🇦"], ["fr", "Français", "French", "🇫🇷"], ["bn", "বাংলা", "Bengali", "🇧🇩"], ["pt", "Português", "Portuguese", "🇧🇷"],
  ["ru", "Русский", "Russian", "🇷🇺"], ["ur", "اردو", "Urdu", "🇵🇰"],
];

export const LanguageSelector = ({ tint = "#FFFFFF" }) => {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState(() => localStorage.getItem("alr_language") || "en");
  const meta = LANGS.find((l) => l[0] === lang) || LANGS[0];
  const pick = (code) => { localStorage.setItem("alr_language", code); setLang(code); setOpen(false); };
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="language-selector" aria-label="Choose language" className="flex h-10 items-center gap-1.5 rounded-lg border border-[#333] bg-[#1A1A1A] px-2.5">
        <span className="mr-0.5 text-base">{meta[3]}</span><Ion name="globe-outline" size={18} color={tint} />
      </button>
      {open && (
        <div className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/60" onClick={() => setOpen(false)}>
          <div className="max-h-[80%] w-full max-w-[640px] overflow-y-auto rounded-t-[20px] border border-[#333] bg-[#0A0A0A] p-6" onClick={(e) => e.stopPropagation()} data-testid="language-sheet">
            <div className="mb-4 flex items-center justify-between"><p className="text-lg font-extrabold">Choose Language</p><button onClick={() => setOpen(false)} data-testid="language-close"><Ion name="close" size={22} color="#fff" /></button></div>
            {LANGS.map(([code, name, en, flag]) => (
              <button key={code} onClick={() => pick(code)} data-testid={`lang-${code}`} className={`flex w-full items-center gap-4 border-b border-[#333] px-2 py-3.5 text-left ${code === lang ? "bg-white/[0.03]" : ""}`}>
                <span className="text-[28px]">{flag}</span>
                <span className="flex-1"><span className="block text-base font-bold" style={{ color: code === lang ? colors.accent : "#fff" }}>{name}</span><span className="mt-0.5 block text-xs text-[#A0A0A0]">{en}</span></span>
                {code === lang && <Ion name="checkmark-circle" size={20} color={colors.accent} />}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};
